import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { requestPasswordReset, resetPassword } from '../../api/client';
import { ApiProblem } from '../../api/problem';
import { PUBLIC_ROUTES } from '../../routes';
import { useSession } from '../../session/useSession';
import { AuthShell, PasswordInput, PasswordStrength } from '../auth/AuthShell';
import { Alert, CheckedForm, describedBy, Field, fieldErrors, LIMITS, problemMessage, TextField } from '../form';
import { Icon } from '../Icon';
import { AuthNotice } from '../SignInPage';

/**
 * Les quatre écrans du mot de passe.
 *
 *   ForcedPasswordPage   D32 — remplace toute l'application tant qu'un
 *                        provisoire n'est pas changé ;
 *   ChangePasswordScreen D30 — le changement volontaire, dans la coquille ;
 *   ForgotPasswordPage   D30 — public, ne renseigne jamais ;
 *   SetPasswordPage      D30/D32 — public, ouvert par le lien du courriel.
 *
 * Les deux premiers partagent le même formulaire et la même issue : toutes les
 * sessions sont coupées, et l'on revient à la connexion avec un avis.
 */

export function ChangePasswordForm({ intro }: { readonly intro: string }): ReactNode {
    const { t, changePassword } = useSession();
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [pending, setPending] = useState(false);
    const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
    const [failure, setFailure] = useState<string | null>(null);

    async function submit(): Promise<void> {
        if (next !== confirm) {
            setErrors({ confirm: t('password.mismatch') });

            return;
        }

        setPending(true);
        setErrors({});
        setFailure(null);

        try {
            // En cas de succès, la session passe à « anonyme » : ce composant
            // est démonté, et l'écran de connexion affiche l'avis.
            await changePassword(current, next);
        } catch (cause) {
            setErrors(fieldErrors(t, cause));
            setFailure(cause instanceof ApiProblem && cause.status === 422 ? null : problemMessage(t, cause));
            setPending(false);
        }
    }

    return (
        <CheckedForm className="stack" onSubmit={() => void submit()}>
            <p className="card__body">{intro}</p>

            {failure === null ? null : <Alert tone="error">{failure}</Alert>}

            <Field id="current-password" label={t('password.current')} error={errors.currentPassword} required>
                <input
                    id="current-password"
                    className="field__input"
                    type="password"
                    autoComplete="current-password"
                    required
                    aria-required={true}
                    value={current}
                    onChange={(event) => setCurrent(event.target.value)}
                    {...describedBy('current-password', false, errors.currentPassword)}
                />
            </Field>

            <Field id="new-password" label={t('password.new')} hint={t('password.rule')} error={errors.newPassword} required>
                <input
                    id="new-password"
                    className="field__input"
                    type="password"
                    autoComplete="new-password"
                    required
                    aria-required={true}
                    minLength={12}
                    value={next}
                    onChange={(event) => setNext(event.target.value)}
                    {...describedBy('new-password', true, errors.newPassword)}
                />
            </Field>

            <Field id="confirm-password" label={t('password.confirm')} error={errors.confirm} required>
                <input
                    id="confirm-password"
                    className="field__input"
                    type="password"
                    autoComplete="new-password"
                    required
                    aria-required={true}
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value)}
                    {...describedBy('confirm-password', false, errors.confirm)}
                />
            </Field>

            <div className="actions">
                <button className="button" type="submit" disabled={pending}>
                    {pending ? t('common.saving') : t('password.change.submit')}
                </button>
            </div>
        </CheckedForm>
    );
}

/** D32 — hors coquille, sans menu : il n'y a rien d'autre à faire. */
export function ForcedPasswordPage(): ReactNode {
    const { t, signOut } = useSession();

    return (
        <AuthShell>
            <div className="auth-form">
                <div className="auth-form__head">
                    <h1>{t('password.imposed.title')}</h1>
                </div>
                <ChangePasswordForm intro={t('password.imposed.body')} />
                <button className="link-button auth-form__back" type="button" onClick={() => void signOut()}>
                    {t('session.signOut')}
                </button>
            </div>
        </AuthShell>
    );
}

/** D30 — dans la coquille, depuis la barre supérieure. */
export function ChangePasswordScreen(): ReactNode {
    const { t } = useSession();

    return (
        <section className="card">
            <h1 className="card__title">{t('password.change.title')}</h1>
            <ChangePasswordForm intro={t('password.change.hint')} />
        </section>
    );
}

/**
 * D30 — la réponse affichée est la même que l'adresse existe ou non : c'est le
 * serveur qui décide d'envoyer, et il répond 202 dans tous les cas. Seul un 429
 * (trop de demandes depuis cette machine) se distingue, et il ne dit rien d'un
 * compte.
 */
export function ForgotPasswordPage(): ReactNode {
    const { t } = useSession();
    const [email, setEmail] = useState('');
    const [pending, setPending] = useState(false);
    const [sent, setSent] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});

    async function submit(): Promise<void> {
        setPending(true);
        setFailure(null);
        setErrors({});

        try {
            await requestPasswordReset(email);
            setSent(true);
        } catch (cause) {
            setErrors(fieldErrors(t, cause));

            if (cause instanceof ApiProblem && cause.status === 429) {
                setFailure(t('forgot.tooMany'));
            } else if (!(cause instanceof ApiProblem && cause.status === 422)) {
                setFailure(problemMessage(t, cause));
            }
        } finally {
            setPending(false);
        }
    }

    return (
        <AuthShell>
            {sent ? (
                <div className="auth-form auth-form--done">
                    <span className="auth-form__badge" aria-hidden="true">
                        <Icon name="mail" size={28} />
                    </span>
                    <div className="auth-form__head">
                        <h1>{t('forgot.sentTitle')}</h1>
                        <span className="muted">{t('forgot.sentBody', { email })}</span>
                    </div>
                    <ul className="auth-form__tips">
                        <li>{t('forgot.tip.spam')}</li>
                        <li>{t('forgot.tip.last')}</li>
                        <li>{t('forgot.tip.sessions')}</li>
                    </ul>
                    {failure === null ? null : <AuthNotice tone="warn">{failure}</AuthNotice>}
                    <div className="auth-form__row">
                        <Link className="button button--ghost" to="/">
                            {t('forgot.back')}
                        </Link>
                        <button className="button button--ghost" type="button" disabled={pending} onClick={() => void submit()}>
                            {t('forgot.again')}
                        </button>
                    </div>
                </div>
            ) : (
                <CheckedForm className="auth-form" onSubmit={() => void submit()}>
                    <Link className="auth-form__back" to="/">
                        <Icon name="arrowLeft" size={16} />
                        {t('forgot.back')}
                    </Link>
                    <div className="auth-form__head">
                        <h1>{t('forgot.title')}</h1>
                        <span className="muted">{t('forgot.body')}</span>
                    </div>

                    {failure === null ? null : <AuthNotice tone="warn">{failure}</AuthNotice>}

                    <TextField id="forgot-email" label={t('signIn.email')} type="email" autoComplete="username" limit={LIMITS.UserAccount.email} value={email} onChange={setEmail} error={errors.email} />

                    <button className="button auth-form__submit" type="submit" disabled={pending || email.trim() === ''}>
                        {pending ? t('common.saving') : t('forgot.submit')}
                    </button>

                    <p className="muted auth-form__small">{t('forgot.noAccess')}</p>
                </CheckedForm>
            )}
        </AuthShell>
    );
}

/**
 * Lit le jeton dans le FRAGMENT de l'adresse (`#token=…`).
 *
 * Le fragment n'est jamais envoyé au serveur : il n'apparaît ni dans les
 * journaux d'Apache, ni dans le Referer. La page l'efface ensuite de la barre
 * d'adresse (voir l'effet plus bas) pour qu'il ne reste pas dans l'historique —
 * un poste partagé est la règle dans un établissement, pas l'exception.
 *
 * Fonction PURE, sans l'effacement : StrictMode appelle deux fois l'initialiseur
 * d'un useState, et le second appel trouverait sinon un fragment déjà vidé.
 */
function tokenFromFragment(): string | null {
    const match = /(?:^|[#&])token=([0-9a-f]{64})(?:&|$)/.exec(window.location.hash);

    return match?.[1] ?? null;
}

export function SetPasswordPage(): ReactNode {
    const { t, session, announce } = useSession();
    const navigate = useNavigate();
    const [token] = useState<string | null>(tokenFromFragment);
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [pending, setPending] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
    const [failure, setFailure] = useState<string | null>(null);

    useEffect(() => {
        if (window.location.hash !== '') {
            window.history.replaceState(window.history.state, '', window.location.pathname);
        }
    }, []);

    useEffect(() => {
        document.title = `${t('reset.title')} · ${t('app.name')}`;
    }, [t]);

    async function submit(): Promise<void> {
        if (token === null) {
            return;
        }

        if (next !== confirm) {
            setErrors({ confirm: t('password.mismatch') });

            return;
        }

        setPending(true);
        setErrors({});
        setFailure(null);

        try {
            await resetPassword(token, next);
            announce('passwordSet');
            navigate('/', { replace: true });
        } catch (cause) {
            const fields = fieldErrors(t, cause);

            if (fields.token !== undefined) {
                setInvalid(true);
            } else {
                setErrors(fields);
                setFailure(cause instanceof ApiProblem && cause.status === 422 ? null : problemMessage(t, cause));
            }

            setPending(false);
        }
    }

    const problem = token === null ? t('reset.missing') : invalid ? t('reset.invalid') : null;

    if (problem !== null) {
        return (
            <AuthShell>
                <div className="auth-form auth-form--done">
                    <span className="auth-form__badge auth-form__badge--warn" aria-hidden="true">
                        <Icon name="alert" size={28} />
                    </span>
                    <div className="auth-form__head">
                        <h1>{t('reset.deadTitle')}</h1>
                        <span className="muted">{problem}</span>
                    </div>
                    {session.status === 'anonymous' ? (
                        <Link className="button auth-form__submit" to={PUBLIC_ROUTES.forgotPassword}>
                            {t('reset.askAgain')}
                        </Link>
                    ) : null}
                    <Link className="auth-form__back" to="/">
                        {t('forgot.back')}
                    </Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <CheckedForm className="auth-form" onSubmit={() => void submit()}>
                <div className="auth-form__head">
                    <h1>{t('reset.title')}</h1>
                    <span className="muted">{t('reset.body')}</span>
                </div>

                {failure === null ? null : <AuthNotice tone="danger">{failure}</AuthNotice>}

                <Field id="set-password" label={t('password.new')} error={errors.newPassword} required>
                    <PasswordInput id="set-password" autoComplete="new-password" value={next} onChange={setNext} invalid={errors.newPassword !== undefined} />
                </Field>
                <PasswordStrength value={next} />

                <Field id="set-confirm" label={t('password.confirm')} error={errors.confirm} required>
                    <PasswordInput id="set-confirm" autoComplete="new-password" value={confirm} onChange={setConfirm} invalid={errors.confirm !== undefined} />
                </Field>

                <button className="button auth-form__submit" type="submit" disabled={pending || next.length < 12 || confirm === ''}>
                    {pending ? t('common.saving') : t('reset.submit')}
                </button>

                <AuthNotice tone="info">{t('reset.sessions')}</AuthNotice>
            </CheckedForm>
        </AuthShell>
    );
}
